import type { ExpandedApprovalItem } from 'types/approvals'
import NotesIcon from '@mui/icons-material/Notes'
import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import Paper from 'components/@extended/Paper'
import React, { useState } from 'react'

function formatCurrency(value: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

interface AccrualLedgerTableProps {
  items: ExpandedApprovalItem[]
}

const AccrualLedgerTable: React.FC<AccrualLedgerTableProps> = ({ items }) => {
  const theme = useTheme()
  const [notesDialogOpen, setNotesDialogOpen] = useState(false)
  const [selectedRowNotes, setSelectedRowNotes] = useState<{ notes: string; rowInfo: string }>({
    notes: '',
    rowInfo: '',
  })

  if (!items || items.length === 0) {
    return null
  }

  const handleViewNotes = (item: ExpandedApprovalItem) => {
    const accrualItem = item.accrualLedgerItem
    if (!accrualItem) return

    const rowInfo = `${accrualItem.userFirstName} ${accrualItem.userLastName} | ${accrualItem.periodLabel || 'Unknown Period'} | ${accrualItem.allocationTableName || ''} | ${accrualItem.allocationRowName || ''}`
    setSelectedRowNotes({
      notes: accrualItem.notes || '',
      rowInfo,
    })
    setNotesDialogOpen(true)
  }

  const handleCloseNotesDialog = () => {
    setNotesDialogOpen(false)
    setSelectedRowNotes({ notes: '', rowInfo: '' })
  }

  return (
    <div style={{ width: '100%' }}>
      <Typography variant="h3" mb={2}>
        Accrual Ledger
      </Typography>
      <Paper>
        <TableContainer sx={{ maxHeight: 860, minHeight: 200 }}>
          <Table stickyHeader size="small" aria-label="accrual ledger table">
            <TableHead
              sx={{
                '& th': {
                  borderTop: `1px solid ${theme.palette.divider}`,
                  borderBottom: `2px solid ${theme.palette.divider} !important`,
                },
              }}
            >
              <TableRow>
                <TableCell sx={{ minWidth: 300 }}>Line Item</TableCell>
                <TableCell sx={{ minWidth: 150 }}>Account</TableCell>
                <TableCell align="center" sx={{ minWidth: 150 }}>
                  FY to Date Balance
                </TableCell>
                <TableCell align="center" sx={{ minWidth: 150 }}>
                  Balance Change
                </TableCell>
                <TableCell align="center" sx={{ minWidth: 100 }}>
                  Notes
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, index) => {
                const isDeleted = !!item.accrualLedgerItem?.deletedAt
                const row = (
                  <TableRow
                    key={index}
                    sx={{
                      opacity: isDeleted ? 0.5 : 1,
                      pointerEvents: isDeleted ? 'none' : 'auto',
                    }}
                  >
                    <TableCell>
                      {`${item.accrualLedgerItem?.userFirstName} ${item.accrualLedgerItem?.userLastName} | ${item.accrualLedgerItem?.periodLabel} | ${item.accrualLedgerItem?.allocationTableName || ''} | ${item.accrualLedgerItem?.allocationRowName || ''}`}
                    </TableCell>
                    <TableCell>{item.accrualLedgerItem?.ledgerAccountLabel}</TableCell>
                    <TableCell align="center">{formatCurrency(item.accrualLedgerItem?.balance || 0)}</TableCell>
                    <TableCell align="center">
                      {formatCurrency(
                        (item.accrualLedgerItem?.balance || 0) - (item.accrualLedgerItem?.previousBalance || 0)
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {(() => {
                        const hasNotes = item.accrualLedgerItem?.notes && item.accrualLedgerItem.notes.trim().length > 0
                        return (
                          <Box display="flex" justifyContent="center">
                            <Tooltip title={hasNotes ? 'View notes' : 'No notes available'}>
                              <IconButton
                                size="small"
                                onClick={() => handleViewNotes(item)}
                                sx={{
                                  padding: 0.5,
                                  color: hasNotes ? theme.palette.primary.main : theme.palette.action.disabled,
                                }}
                                disabled={!hasNotes}
                              >
                                <NotesIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )
                      })()}
                    </TableCell>
                  </TableRow>
                )

                return isDeleted ? (
                  <Tooltip title="This item has been deleted" key={index}>
                    <span>{row}</span>
                  </Tooltip>
                ) : (
                  row
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Notes Dialog */}
      <Dialog
        open={notesDialogOpen}
        onClose={handleCloseNotesDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '300px',
          },
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Notes</Typography>
            <IconButton
              aria-label="close"
              onClick={handleCloseNotesDialog}
              sx={{
                color: theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Entry:
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              {selectedRowNotes.rowInfo}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Notes:
            </Typography>
            {selectedRowNotes.notes && selectedRowNotes.notes.trim().length > 0 ? (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                {selectedRowNotes.notes}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No notes available for this entry.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNotesDialog} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default AccrualLedgerTable
